import cronsTrue from "cronstrue";

import {
  CRON_FIELD_BOUNDS,
  CRON_TIMEZONE_PREFIX,
  validateRequiredCronExpression,
} from "utils/formValidators";

export const EMPTY_VALUE = "";

const DAY_OF_MONTH_FIELD_INDEX = 2;

const CRON_FIELD_VALUE_NAMES = [
  undefined,
  undefined,
  undefined,
  ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"],
  ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"],
];

const parseCronValue = (value: string, index: number) => {
  const nameIndex = CRON_FIELD_VALUE_NAMES[index]?.indexOf(value.toUpperCase()) ?? -1;
  return nameIndex === -1 ? Number(value) : CRON_FIELD_BOUNDS[index][0] + nameIndex;
};

// cronsTrue reads a step as "every N", which is untrue when the step matches a single value or doesn't repeat evenly.
const expandMisleadingCronStep = (item: string, index: number) => {
  const [, from, rangeEnd, stepValue] = item.match(/^(\*|\?|\w+)(?:-(\w+))?\/(\d+)$/) ?? [];
  const bounds = CRON_FIELD_BOUNDS[index];
  const step = Number(stepValue);

  if (!bounds || !step) {
    return [item];
  }

  const [lowest, highest] = bounds;
  const start = from === "*" || from === "?" ? lowest : parseCronValue(from, index);
  const end = rangeEnd === undefined ? highest : parseCronValue(rangeEnd, index);

  if (Number.isNaN(start) || Number.isNaN(end)) {
    return [item];
  }

  if (start + step > end) {
    return [String(start)];
  }

  // Month lengths vary, so day-of-month steps are left to cronsTrue, as are ranges, which it already describes
  // as bounded.
  if (index === DAY_OF_MONTH_FIELD_INDEX || rangeEnd !== undefined) {
    return [item];
  }

  if ((highest + 1 - lowest) % step === 0) {
    return [item];
  }

  return Array.from({ length: Math.floor((end - start) / step) + 1 }, (_, valueIndex) =>
    String(start + valueIndex * step)
  );
};

const expandMisleadingCronSteps = (value: string) => {
  const [timezonePrefix = ""] = value.match(CRON_TIMEZONE_PREFIX) ?? [];

  return (
    timezonePrefix +
    value
      .slice(timezonePrefix.length)
      .split(/\s+/)
      .map((field, index) => {
        const items = field.split(",").flatMap((item) => expandMisleadingCronStep(item, index));
        return [...new Set(items)].join(",");
      })
      .join(" ")
  );
};

export const convertCronExpression = (value: string) => {
  const trimmedValue = value.trim();

  if (validateRequiredCronExpression()(trimmedValue) !== true) {
    return undefined;
  }

  // cronsTrue reads H as a literal and garbles the description, so hashed expressions are shown as written.
  if (trimmedValue.split(/\s+/).some((field) => field.startsWith("H"))) {
    return trimmedValue;
  }

  try {
    return cronsTrue.toString(expandMisleadingCronSteps(trimmedValue));
  } catch {
    return undefined;
  }
};
